import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { RunTypeBadge } from '../../components/common/RunTypeBadge';
import { StatusBadge } from '../../components/common/StatusBadge';
import {
  fmtPct,
  MetricSpec,
  OD_METRICS,
  PC_POG_METRICS,
  PC_ROG_METRICS,
  PR_METRICS,
} from '../../metrics';
import { PrCompRunConfig } from '../../pr-compliance/types';
import { EvalApiService } from '../../services/EvalApiService';
import { EvalRun, RunResult, RunType } from '../../types';
import { fmtDuration, fmtIso } from '../../utils';

const MAX_RUNS = 4;

// ─── shared cell styles ──────────────────────────────────────────────────────

const classCell: React.CSSProperties = {
  padding: '6px 10px',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  background: 'var(--surface-raised)',
  borderRight: '1px solid var(--border)',
  borderBottom: '1px solid var(--divider)',
  textAlign: 'center',
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
};

const labelCell: React.CSSProperties = {
  padding: '7px 12px',
  fontSize: 12,
  color: 'var(--text-secondary)',
  borderBottom: '1px solid var(--divider)',
  whiteSpace: 'nowrap',
  minWidth: 120,
};

const valueCell: React.CSSProperties = {
  padding: '7px 12px',
  fontFamily: 'var(--font-mono)',
  fontSize: 12,
  borderBottom: '1px solid var(--divider)',
  borderLeft: '1px solid var(--border)',
  textAlign: 'right',
};

// ─── row components ───────────────────────────────────────────────────────────

/** Section divider spanning all columns (class col + metric col + N run cols). */
const SectionHeader: React.FC<{ label: string; cols: number }> = ({ label, cols }) => (
  <tr>
    <td
      colSpan={cols + 2}
      style={{
        padding: '10px 12px 4px',
        fontSize: 11,
        fontWeight: 700,
        color: 'var(--text-secondary)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        background: 'var(--table-header-bg)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      {label}
    </td>
  </tr>
);

/** Plain text row (info section). Spans the class+metric columns with colSpan=2. */
const TextRow: React.FC<{ label: string; values: (React.ReactNode | null | undefined)[] }> = ({
  label,
  values,
}) => (
  <tr className="eval-compare-row">
    <td colSpan={2} style={labelCell}>
      {label}
    </td>
    {values.map((v, i) => (
      <td
        key={i}
        style={{ ...valueCell, fontFamily: 'var(--font-mono)', fontSize: 11, textAlign: 'left' }}
      >
        {v ?? '—'}
      </td>
    ))}
  </tr>
);

/** Flat metric row without class grouping (used for PC). Spans class+metric columns. */
const MetricRow: React.FC<{
  label: string;
  values: (number | null | undefined)[];
  fmt?: (v: number | null | undefined) => string;
  lowerIsBetter?: boolean;
  // Raw counts (e.g. # outs) have no "better" direction — skip the per-row highlight.
  noHighlight?: boolean;
}> = ({ label, values, fmt = fmtPct, lowerIsBetter, noHighlight }) => {
  const nums = values.filter((v): v is number => v != null);
  const bestVal =
    noHighlight || !nums.length ? null : lowerIsBetter ? Math.min(...nums) : Math.max(...nums);
  return (
    <tr className="eval-compare-row">
      <td colSpan={2} style={labelCell}>
        {label}
      </td>
      {values.map((v, i) => {
        const isAccent = v != null && bestVal != null && v === bestVal;
        return (
          <td
            key={i}
            style={{
              ...valueCell,
              color: isAccent ? 'var(--accent)' : 'var(--text-primary)',
              fontWeight: isAccent ? 700 : 400,
            }}
          >
            {fmt(v)}
          </td>
        );
      })}
    </tr>
  );
};

/** Groups consecutive specs by classGroup and renders them with a rowspan class cell. */
function SpecRows({ specs, runs }: { specs: MetricSpec[]; runs: (EvalRun | null)[] }) {
  // Build groups of consecutive specs sharing the same classGroup
  type Group = { cls: string | undefined; items: MetricSpec[] };
  const groups: Group[] = [];
  for (const spec of specs) {
    const last = groups[groups.length - 1];
    if (last && last.cls === spec.classGroup) {
      last.items.push(spec);
    } else {
      groups.push({ cls: spec.classGroup, items: [spec] });
    }
  }

  return (
    <>
      {groups.flatMap(({ cls, items }) =>
        items.map((m, idx) => {
          const isLast = idx === items.length - 1;
          const rowBorder = isLast ? '2px solid var(--border)' : '1px solid var(--divider)';
          const values = runs.map((r) => (r?.result ? m.getValue(r.result) : undefined));
          const nums = values.filter((v): v is number => v != null);
          const bestVal = nums.length
            ? m.lowerIsBetter
              ? Math.min(...nums)
              : Math.max(...nums)
            : null;
          const fmtFn = m.fmt ?? fmtPct;

          return (
            <tr key={m.label} className="eval-compare-row">
              {cls === undefined ? (
                // No class grouping — span both columns
                <td colSpan={2} style={{ ...labelCell, borderBottom: rowBorder }}>
                  {m.shortLabel ?? m.label}
                </td>
              ) : (
                <>
                  {idx === 0 && (
                    <td
                      rowSpan={items.length}
                      style={{ ...classCell, borderBottom: '2px solid var(--border)' }}
                    >
                      {cls}
                    </td>
                  )}
                  <td style={{ ...labelCell, borderBottom: rowBorder }}>
                    {m.shortLabel ?? m.label}
                  </td>
                </>
              )}
              {values.map((v, i) => {
                const isAccent = v != null && bestVal != null && v === bestVal;
                return (
                  <td
                    key={i}
                    style={{
                      ...valueCell,
                      borderBottom: rowBorder,
                      color: isAccent ? 'var(--accent)' : 'var(--text-primary)',
                      fontWeight: isAccent ? 700 : 400,
                    }}
                  >
                    {fmtFn(v)}
                  </td>
                );
              })}
            </tr>
          );
        })
      )}
    </>
  );
}

function getMetricValues<T>(
  runs: (EvalRun | null)[],
  getter: (r: RunResult) => T
): (T | undefined)[] {
  return runs.map((r) => (r?.result ? getter(r.result) : undefined));
}

/** Short solution tag for a run's column header (e.g. "Core Model · low=out", "PC v2"). */
function solutionLabel(run: EvalRun): string | null {
  const cfg = run.config as unknown as PrCompRunConfig | undefined;
  if (cfg?.solution_type === 'legacy_match_oos') {
    return `Core Model · ${cfg.low_as_out ? 'low=out' : 'low=in'}`;
  }
  if (cfg?.solution_type === 'pc_v2') {
    return 'PC v2';
  }
  return null;
}

/** Raw legacy state histogram (in/low/out/n/a) stored on legacy import runs, else null. */
function legacyStateCounts(run: EvalRun | null): Record<string, number> | null {
  if (!run) return null;
  const cfg = run.config as unknown as PrCompRunConfig | undefined;
  return cfg?.legacy_state_counts ?? null;
}

/** Format an integer count; em-dash for missing. */
const fmtCount = (v: number | null | undefined): string => (v == null ? '—' : String(v));

function keyModelForSourceRun(sourceRun: EvalRun, forRunType: RunType): string | null {
  if (forRunType === 'PR') {
    return sourceRun.model_info.OD?.model_name ?? sourceRun.model_ids.OD;
  }
  if (forRunType === 'PC') {
    return sourceRun.model_info.VE?.model_name ?? sourceRun.model_ids.VE;
  }
  return null;
}

export const CompareRunsPage: React.FC = () => {
  const { token } = useAuthContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialIds = (searchParams.get('runs') ?? '').split(',').filter(Boolean).slice(0, MAX_RUNS);
  const [runIds, setRunIds] = useState<string[]>(() => {
    // Always show MAX_RUNS slots; pad URL-supplied IDs with empties.
    const padded = [...initialIds];
    while (padded.length < MAX_RUNS) padded.push('');
    return padded.slice(0, MAX_RUNS);
  });
  const [runs, setRuns] = useState<(EvalRun | null)[]>([null, null, null, null]);
  const [allRuns, setAllRuns] = useState<EvalRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [sourceRunMap, setSourceRunMap] = useState<Record<string, EvalRun>>({});

  useEffect(() => {
    EvalApiService.listRuns(token ?? '').then(({ items }) => setAllRuns(items));
  }, [token]);

  useEffect(() => {
    const ids = runIds.filter(Boolean);
    if (!ids.length) return;
    setLoading(true);
    Promise.all(ids.map((id) => EvalApiService.getRun(token ?? '', id).catch(() => null)))
      .then(async (fetched) => {
        // For PR Compliance runs (config.solution_type set) the backend never
        // populates result.PR_COMP — it computes precision/recall on demand via
        // /pr-comp-metrics. Fetch that here and project it onto result.PR_COMP
        // so the existing metric rows render without any other code changes.
        const enriched = await Promise.all(
          fetched.map(async (r) => {
            if (!r) return r;
            const cfg = r.config as unknown as PrCompRunConfig | undefined;
            if (!cfg?.solution_type) return r;
            const metrics = await EvalApiService.getRunPrCompMetrics(token ?? '', r.run_id).catch(
              () => null
            );
            if (!metrics) return r;
            return {
              ...r,
              result: {
                ...(r.result ?? {}),
                PR_COMP: {
                  folded: metrics.folded,
                  out_only: metrics.out_only,
                  faceover_only: metrics.faceover_only,
                  stability: null,
                  pred_counts: metrics.pred_counts,
                },
              },
            } as EvalRun;
          })
        );

        const padded: (EvalRun | null)[] = [null, null, null, null];
        enriched.forEach((r, i) => {
          padded[i] = r;
        });
        setRuns(padded);

        const sourceIds = [
          ...new Set(
            enriched
              .filter(Boolean)
              .map((r) => r!.source_run_id)
              .filter(Boolean)
          ),
        ] as string[];
        if (sourceIds.length) {
          Promise.all(
            sourceIds.map((id) => EvalApiService.getRun(token ?? '', id).catch(() => null))
          ).then((results) => {
            const map: Record<string, EvalRun> = {};
            results.forEach((r) => {
              if (r) map[r.run_id] = r;
            });
            setSourceRunMap(map);
          });
        } else {
          setSourceRunMap({});
        }
      })
      .finally(() => setLoading(false));
  }, [runIds, token]);

  const updateId = (index: number, id: string) => {
    setRunIds((prev) => {
      const next = [...prev];
      next[index] = id;
      return next;
    });
    const ids = [...runIds];
    ids[index] = id;
    const nonEmpty = ids.filter(Boolean);
    setSearchParams(nonEmpty.length ? { runs: nonEmpty.join(',') } : {});
  };

  const addSlot = () => {
    if (runIds.length < MAX_RUNS) setRunIds((prev) => [...prev, '']);
  };

  const removeSlot = (index: number) => {
    setRunIds((prev) =>
      prev
        .filter((_, i) => i !== index)
        .concat(prev.length <= 2 ? [''] : [])
        .slice(0, Math.max(2, prev.length - 1))
    );
    setRuns((prev) => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
  };

  const activeRuns = runs.slice(0, runIds.length);
  const cols = runIds.length;

  const hasOD = activeRuns.some((r) => r?.result?.OD);
  const hasPR = activeRuns.some((r) => r?.result?.PR);
  const hasPC = activeRuns.some((r) => r?.result?.PC);
  const hasPrComp = activeRuns.some((r) => r?.result?.PR_COMP);
  const hasStability = activeRuns.some((r) => r?.result?.PR_COMP?.stability != null);
  const hasLegacyCounts = activeRuns.some((r) => legacyStateCounts(r));

  return (
    <div className="page-content">
      <div className="eval-page-header">
        <div>
          <div className="eval-page-title">Compare Runs</div>
          <div className="eval-page-sub">Side-by-side metrics for up to {MAX_RUNS} runs</div>
        </div>
        <button
          className="eval-btn"
          onClick={addSlot}
          disabled={runIds.length >= MAX_RUNS}
          title={runIds.length >= MAX_RUNS ? `Maximum of ${MAX_RUNS} runs reached` : undefined}
          style={runIds.length >= MAX_RUNS ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
        >
          + Add Run
        </button>
      </div>

      {/* Run selectors */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {runIds.map((id, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <select
              className="eval-filter-select"
              value={id}
              onChange={(e) => updateId(i, e.target.value)}
              style={{ minWidth: 220, fontFamily: 'var(--font-mono)', fontSize: 12 }}
            >
              <option value="">Run {i + 1}…</option>
              {allRuns.map((r) => {
                const cfg = r.config as unknown as PrCompRunConfig | undefined;
                // Distinguish the two legacy runs an import produces by their `low` interpretation.
                let solSuffix = '';
                if (cfg?.solution_type === 'legacy_match_oos') {
                  solSuffix = cfg.low_as_out ? ' · legacy low=out' : ' · legacy low=in';
                } else if (cfg?.solution_type === 'pc_v2') {
                  solSuffix = ' · PC v2';
                }
                return (
                  <option key={r.run_id} value={r.run_id}>
                    {r.run_id} ({r.run_type} · {r.status}){solSuffix}
                  </option>
                );
              })}
            </select>
            {runIds.length > 2 && (
              <button
                className="eval-btn-danger"
                style={{ padding: '4px 8px', fontSize: 11 }}
                onClick={() => removeSlot(i)}
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>

      {loading && <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading…</div>}

      {!loading && activeRuns.some((r) => r !== null) && (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              background: 'var(--surface)',
              borderRadius: 8,
              border: '1px solid var(--border)',
              overflow: 'hidden',
            }}
          >
            <thead>
              <tr style={{ background: 'var(--table-header-bg)' }}>
                <th
                  style={{
                    padding: '8px 12px',
                    textAlign: 'left',
                    color: 'var(--text-secondary)',
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid var(--border)',
                    minWidth: 80,
                  }}
                >
                  Class
                </th>
                <th
                  style={{
                    padding: '8px 12px',
                    textAlign: 'left',
                    color: 'var(--text-secondary)',
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid var(--border)',
                    minWidth: 140,
                  }}
                >
                  Metric
                </th>
                {activeRuns.map((run, i) => (
                  <th
                    key={i}
                    style={{
                      padding: '8px 12px',
                      borderBottom: '1px solid var(--border)',
                      borderLeft: '1px solid var(--border)',
                      minWidth: 180,
                    }}
                  >
                    {run ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <Link
                          to={`/eval/runs/${run.run_id}`}
                          style={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: 11,
                            color: 'var(--accent)',
                            textDecoration: 'none',
                          }}
                        >
                          {run.run_id}
                        </Link>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <RunTypeBadge runType={run.run_type} />
                          <StatusBadge status={run.status} />
                        </div>
                        {solutionLabel(run) && (
                          <span
                            style={{
                              alignSelf: 'flex-start',
                              fontSize: 10,
                              fontWeight: 600,
                              padding: '1px 6px',
                              borderRadius: 4,
                              border: '1px solid var(--border)',
                              background: 'var(--surface-raised)',
                              color: 'var(--text-secondary)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {solutionLabel(run)}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>—</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <SectionHeader label="Info" cols={cols} />
              <TextRow
                label="Dataset"
                values={activeRuns.map((r) => (r ? (r.dataset_name ?? r.dataset_id) : null))}
              />
              <TextRow
                label="Base Run"
                values={activeRuns.map((r) => {
                  if (!r) return null;
                  if (!r.source_run_id) return 'GT';
                  const srcRun = sourceRunMap[r.source_run_id];
                  const modelName = srcRun ? keyModelForSourceRun(srcRun, r.run_type) : null;
                  return (
                    <div>
                      <div style={{ wordBreak: 'break-all' }}>{modelName ?? r.source_run_id}</div>
                      {modelName && (
                        <div
                          style={{
                            fontSize: 10,
                            color: 'var(--text-tertiary)',
                            marginTop: 2,
                            wordBreak: 'break-all',
                          }}
                        >
                          {r.source_run_id}
                        </div>
                      )}
                    </div>
                  );
                })}
              />
              <TextRow
                label="OD Model"
                values={activeRuns.map(
                  (r) => r?.model_info.OD?.model_name ?? r?.model_ids.OD ?? null
                )}
              />
              <TextRow
                label="VE Model"
                values={activeRuns.map(
                  (r) => r?.model_info.VE?.model_name ?? r?.model_ids.VE ?? null
                )}
              />
              <TextRow
                label="PC Model"
                values={activeRuns.map((r) =>
                  r?.model_info.PC
                    ? `${r.model_info.PC.version} · ${r.model_info.PC.model_name}`
                    : (r?.model_ids.PC ?? null)
                )}
              />
              <TextRow
                label="Triggered by"
                values={activeRuns.map((r) => r?.triggered_by.split('@')[0])}
              />
              <TextRow
                label="Triggered at"
                values={activeRuns.map((r) => fmtIso(r?.triggered_at))}
              />
              <TextRow
                label="Duration"
                values={activeRuns.map((r) =>
                  r ? fmtDuration(r.started_at, r.completed_at) : null
                )}
              />

              {hasOD && (
                <>
                  <SectionHeader label="OD" cols={cols} />
                  <SpecRows specs={OD_METRICS} runs={activeRuns} />
                </>
              )}

              {hasPR && (
                <>
                  <SectionHeader label="PR" cols={cols} />
                  <SpecRows specs={PR_METRICS} runs={activeRuns} />
                </>
              )}

              {hasPC && (
                <>
                  <SectionHeader label="PC — POG" cols={cols} />
                  {PC_POG_METRICS.map((m) => (
                    <MetricRow
                      key={m.label}
                      label={m.label}
                      values={getMetricValues(activeRuns, m.getValue)}
                      fmt={m.fmt}
                      lowerIsBetter={m.lowerIsBetter}
                    />
                  ))}
                  <SectionHeader label="PC — ROG" cols={cols} />
                  {PC_ROG_METRICS.map((m) => (
                    <MetricRow
                      key={m.label}
                      label={m.label}
                      values={getMetricValues(activeRuns, m.getValue)}
                      fmt={m.fmt}
                      lowerIsBetter={m.lowerIsBetter}
                    />
                  ))}
                </>
              )}

              {hasPrComp && (
                <>
                  <SectionHeader label="PR Compliance — All restock-needed slots" cols={cols} />
                  <MetricRow
                    label="Precision"
                    values={getMetricValues(activeRuns, (r) => r.PR_COMP?.folded.precision)}
                  />
                  <MetricRow
                    label="Recall"
                    values={getMetricValues(activeRuns, (r) => r.PR_COMP?.folded.recall)}
                  />
                  <MetricRow
                    label="F1"
                    values={getMetricValues(activeRuns, (r) => r.PR_COMP?.folded.f1)}
                  />

                  <SectionHeader label="PR Compliance — Out only" cols={cols} />
                  <MetricRow
                    label="Precision"
                    values={getMetricValues(activeRuns, (r) => r.PR_COMP?.out_only.precision)}
                  />
                  <MetricRow
                    label="Recall"
                    values={getMetricValues(activeRuns, (r) => r.PR_COMP?.out_only.recall)}
                  />
                  <MetricRow
                    label="F1"
                    values={getMetricValues(activeRuns, (r) => r.PR_COMP?.out_only.f1)}
                  />

                  <SectionHeader label="PR Compliance — Faceover only" cols={cols} />
                  <MetricRow
                    label="Precision"
                    values={getMetricValues(
                      activeRuns,
                      (r) => r.PR_COMP?.faceover_only?.precision ?? null
                    )}
                  />
                  <MetricRow
                    label="Recall"
                    values={getMetricValues(
                      activeRuns,
                      (r) => r.PR_COMP?.faceover_only?.recall ?? null
                    )}
                  />

                  {hasStability && (
                    <>
                      <SectionHeader
                        label="PR Compliance — Temporal stability (sequence runs)"
                        cols={cols}
                      />
                      <MetricRow
                        label="Stability"
                        values={getMetricValues(activeRuns, (r) => r.PR_COMP?.stability ?? null)}
                      />
                    </>
                  )}

                  <SectionHeader label="Predicted classes (count)" cols={cols} />
                  {(['compliant', 'out', 'faceover'] as const).map((cls) => (
                    <MetricRow
                      key={cls}
                      label={cls === 'compliant' ? 'Compliant' : cls === 'out' ? 'Out' : 'Faceover'}
                      values={activeRuns.map((r) => r?.result?.PR_COMP?.pred_counts?.[cls] ?? null)}
                      fmt={fmtCount}
                      noHighlight
                    />
                  ))}

                  {hasLegacyCounts && (
                    <>
                      <SectionHeader label="Core Model raw states (count)" cols={cols} />
                      {(
                        [
                          ['in', 'In'],
                          ['low', 'Low'],
                          ['out', 'Out'],
                          ['n/a', 'N/A'],
                        ] as const
                      ).map(([key, label]) => (
                        <MetricRow
                          key={key}
                          label={label}
                          values={activeRuns.map((r) => legacyStateCounts(r)?.[key] ?? null)}
                          fmt={fmtCount}
                          noHighlight
                        />
                      ))}
                    </>
                  )}
                </>
              )}
            </tbody>
          </table>

          <div
            style={{
              marginTop: 8,
              fontSize: 11,
              color: 'var(--text-tertiary)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            <span style={{ color: 'var(--accent)' }}>highlighted</span> = best value per row
          </div>
        </div>
      )}

      {!loading && activeRuns.every((r) => r === null) && runIds.some(Boolean) && (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
          No runs found for the selected IDs.
        </div>
      )}
    </div>
  );
};
